import { apiRoutes } from '@/constants/api-routes';
import { getMobilePlatform } from '@/lib/device';
import type { PlanTier, SubscriptionDiscountView, SubscriptionView } from '@/types/api';
import { apiRequest } from './http';

/**
 * Body for `POST /billing/checkout-session`. Mirrors the server's
 * `CreateCheckoutSessionDto` — only paid tiers are valid (the server
 * rejects `'free'` with a 400 because there's no Stripe price to
 * subscribe to).
 */
export interface CreateCheckoutSessionPayload {
  tier: Exclude<PlanTier, 'free'>;
  /** Billing cadence. Omitted = monthly (the server's default). */
  interval?: 'month' | 'year';
}

/**
 * Body for `POST /billing/portal-session`. Optional `intent` deep-links
 * the resulting Stripe Customer Portal session straight to a specific
 * flow rather than the portal home screen:
 *
 *   - `'manage'` (default) — portal home; user chooses what to do.
 *   - `'update'` — opens directly on the plan-switch screen for the
 *     account's active subscription. Used by the Pricing page when a
 *     paid subscriber clicks a different paid tier.
 *   - `'cancel'` — opens directly on the cancellation confirmation
 *     screen. Used by the Pricing page when a paid subscriber clicks
 *     the Free card.
 */
export interface CreatePortalSessionPayload {
  intent?: 'manage' | 'update' | 'cancel';
}

export interface RedirectUrl {
  url: string;
}

/**
 * Stripe billing service wrapper. Three endpoints, no business logic on
 * the client — Stripe is the source of truth, the server mirrors state,
 * and the UI just initiates flows + reads back the mirrored payload.
 *
 * Both Checkout and Portal return a hosted URL; callers should
 * `window.location.assign(url)` to hand the browser off to Stripe.
 */
export const billingService = {
  /**
   * Read the mirrored subscription state for the current tenant.
   * Equivalent to reading `account.subscription` off `AccountView` —
   * useful for refetching after a Checkout completes (the webhook may
   * not have landed yet by the time the user lands back on
   * `/account?checkout=success`).
   */
  getSubscription() {
    return apiRequest<SubscriptionView>(apiRoutes.billing.subscription);
  },

  /**
   * The discount/coupon currently applied to the subscription, or `null`.
   * Round-trips to Stripe server-side (discounts aren't mirrored locally), so
   * the Account billing card calls this on mount rather than folding it into
   * the cheap `getSubscription` read.
   */
  getDiscount() {
    return apiRequest<SubscriptionDiscountView | null>(apiRoutes.billing.discount);
  },

  /**
   * Start a Stripe Checkout session for the requested paid tier. Returns
   * the URL to redirect to. The page that consumes this should NOT
   * `await` it then call `setLoading(false)` — once `window.location`
   * navigates, the React tree is being torn down anyway.
   */
  createCheckoutSession(payload: CreateCheckoutSessionPayload) {
    // Flag mobile-browser checkouts so the server returns the user to the
    // mobile-allowed confirmation page (the web app is gated on phones).
    return apiRequest<RedirectUrl>(apiRoutes.billing.checkoutSession, {
      method: 'POST',
      body: { ...payload, mobile: getMobilePlatform() !== null },
    });
  },

  /**
   * Mint a Customer Portal session URL so the user can cancel, swap
   * tiers, or update their payment method. Server returns 400 if the
   * account has no Stripe customer yet; UI should hide the entry point
   * for users without a `stripeCustomerId` (i.e. free-tier accounts that
   * have never started a Checkout).
   *
   * Pass `intent: 'update'` to deep-link into the plan-switch screen
   * (one-click downgrade/upgrade between paid tiers) or `'cancel'` to
   * deep-link into the cancellation flow. Omit for the portal home page.
   */
  createPortalSession(payload: CreatePortalSessionPayload = {}) {
    return apiRequest<RedirectUrl>(apiRoutes.billing.portalSession, {
      method: 'POST',
      body: payload,
    });
  },
};
